/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Message, Tag } from 'rsuite';
import { toast } from 'react-toastify';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiCpu,
  FiExternalLink,
  FiFileText,
  FiRefreshCw,
  FiSend,
  FiXCircle,
} from 'react-icons/fi';
import api from '~/services/api';
import { Bars } from '~/components/loading-ui/bars';
import {
  AnalysisLoading,
  Card,
  CheckResult,
  Checklist,
  ContentLayout,
  DetailsColumn,
  DocumentColumn,
  DocumentImage,
  DocumentLink,
  DocumentPdf,
  DocumentPlaceholder,
  DocumentPreview,
  FeedbackActions,
  FeedbackSection,
  Header,
  HeaderActions,
  HeaderTitle,
  Metadata,
  MetadataItem,
  Problems,
  Reason,
  SectionTitle,
} from './styles';

const STATUS = {
  approved: { texto: 'IA recomenda aprovar', cor: 'green' },
  not_approved: { texto: 'IA recomenda não aprovar', cor: 'red' },
  processing: { texto: 'Processando', cor: 'blue' },
  pending: { texto: 'Pendente', cor: 'orange' },
  error: { texto: 'Erro no processamento', cor: 'red' },
};

const VERIFICACOES = [
  ['documento_legivel', 'Documento legível'],
  ['documento_completo', 'Documento completo'],
  ['nome_confere', 'Nome confere'],
  ['data_nascimento_confere', 'Data de nascimento confere'],
  ['cpf_confere', 'CPF confere'],
];

const MOTIVOS = {
  none: '—',
  mismatch: 'Diverge dos dados informados no cadastro',
  partial_match: 'Compatível, mas abreviado ou incompleto',
  not_visible: 'Não está visível ou não pôde ser lido',
  not_present: 'Este modelo de documento não apresenta o campo',
  uncertain: 'Leitura possível, mas com baixa segurança',
};

const MOTIVOS_ATENCAO = new Set(['partial_match', 'not_present', 'uncertain']);

function obterEstadoVerificacao(aprovado, motivo) {
  if (aprovado) return 'approved';
  if (MOTIVOS_ATENCAO.has(motivo)) return 'attention';
  return 'failed';
}

export default function AnaliseDocumentoCard({
  idProfissional,
  analise,
  documento,
  onAnaliseAtualizada,
}) {
  const [analiseExibida, setAnaliseExibida] = useState(analise);
  const [feedback, setFeedback] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [solicitandoAnalise, setSolicitandoAnalise] = useState(false);
  const [ultimoFeedback, setUltimoFeedback] = useState();
  const onAnaliseAtualizadaRef = useRef(onAnaliseAtualizada);
  const resultado = analiseExibida && analiseExibida.resultado;
  const idAnalise = analiseExibida && analiseExibida.id;
  const feedbackSugerido = resultado && resultado.feedback_sugerido;
  const statusAnalise = analiseExibida && analiseExibida.status;
  const status = STATUS[statusAnalise] || {
    texto: 'Sem análise',
    cor: 'default',
  };
  const caminhoDocumento = documento && documento.caminho;
  const nomeDocumento = (documento && documento.arquivo) || 'Documento';
  const documentoPdf = caminhoDocumento
    ? /\.pdf(?:$|\?)/i.test(caminhoDocumento) || /\.pdf$/i.test(nomeDocumento)
    : false;
  const analiseEmAndamento =
    statusAnalise === 'pending' || statusAnalise === 'processing';
  let textoBotaoAnalise = 'Analisar documento';
  if (analiseEmAndamento) textoBotaoAnalise = 'Análise em andamento';
  else if (statusAnalise) textoBotaoAnalise = 'Refazer análise';

  useEffect(() => {
    onAnaliseAtualizadaRef.current = onAnaliseAtualizada;
  }, [onAnaliseAtualizada]);

  useEffect(() => {
    setFeedback(feedbackSugerido || '');
  }, [idAnalise, feedbackSugerido]);

  useEffect(() => {
    setAnaliseExibida(analise);
  }, [analise]);

  useEffect(() => {
    let ativo = true;
    let consultando = false;

    if (
      !idProfissional ||
      (statusAnalise !== 'pending' && statusAnalise !== 'processing')
    ) {
      return undefined;
    }

    const intervalo = setInterval(async () => {
      if (consultando) return;
      consultando = true;
      try {
        const { data } = await api.get(`/profissional/${idProfissional}`);
        if (ativo) {
          setAnaliseExibida(data.analise_documento);
          if (onAnaliseAtualizadaRef.current) {
            onAnaliseAtualizadaRef.current(data.analise_documento);
          }
        }
      } catch (_) {
        // Mantém o estado atual; uma próxima consulta tentará novamente.
      } finally {
        consultando = false;
      }
    }, 5000);

    return () => {
      ativo = false;
      clearInterval(intervalo);
    };
  }, [idProfissional, statusAnalise]);

  useEffect(() => {
    let ativo = true;
    if (!idProfissional) return undefined;

    api
      .get(`/profissional/${idProfissional}/feedbacks-cadastro`)
      .then(({ data }) => {
        if (ativo) setUltimoFeedback(data[0]);
      })
      .catch(() => {});

    return () => {
      ativo = false;
    };
  }, [idProfissional]);

  const verificacoes = useMemo(
    () =>
      VERIFICACOES.map(([chave, rotulo]) => {
        const aprovado = Boolean(
          resultado && resultado.verificacoes
            ? resultado.verificacoes[chave]
            : false
        );
        const motivo =
          resultado && resultado.motivos_verificacoes
            ? resultado.motivos_verificacoes[chave]
            : undefined;

        return {
          chave,
          rotulo,
          aprovado,
          motivo,
          estado: obterEstadoVerificacao(aprovado, motivo),
        };
      }),
    [resultado]
  );

  async function solicitarAnalise() {
    setSolicitandoAnalise(true);
    try {
      const { data } = await api.post(
        `/profissional/${idProfissional}/analise-documento`
      );
      setAnaliseExibida(data.analise);
      if (onAnaliseAtualizadaRef.current) {
        onAnaliseAtualizadaRef.current(data.analise);
      }
      toast.success('Análise solicitada. O resultado aparecerá neste card.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSolicitandoAnalise(false);
    }
  }

  async function enviarFeedback() {
    if (feedback.trim().length < 5) {
      toast.error('Informe um feedback com pelo menos 5 caracteres');
      return;
    }

    setEnviando(true);
    try {
      const { data } = await api.post(
        `/profissional/${idProfissional}/feedbacks-cadastro`,
        { mensagem: feedback.trim() }
      );
      setUltimoFeedback(data);
      toast.success('Feedback disponibilizado no app do profissional');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card
      bordered
      header={
        <Header>
          <HeaderTitle>
            <FiCpu size={20} />
            <strong>Análise IA do documento</strong>
          </HeaderTitle>
          <HeaderActions>
            <Tag color={status.cor}>{status.texto}</Tag>
            <Button
              type="button"
              appearance="primary"
              startIcon={<FiRefreshCw />}
              loading={solicitandoAnalise}
              disabled={analiseEmAndamento}
              onClick={solicitarAnalise}
            >
              {textoBotaoAnalise}
            </Button>
          </HeaderActions>
        </Header>
      }
    >
      <ContentLayout>
        <DocumentColumn>
          <SectionTitle>Documento enviado</SectionTitle>
          <DocumentPreview>
            {!caminhoDocumento && (
              <DocumentPlaceholder>
                <FiFileText size={34} />
                Documento não disponível para visualização
              </DocumentPlaceholder>
            )}
            {caminhoDocumento && documentoPdf && (
              <DocumentPdf src={caminhoDocumento} title={nomeDocumento} />
            )}
            {caminhoDocumento && !documentoPdf && (
              <DocumentImage src={caminhoDocumento} alt={nomeDocumento} />
            )}
          </DocumentPreview>
          {caminhoDocumento && (
            <DocumentLink
              href={caminhoDocumento}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiExternalLink /> Abrir documento em nova aba
            </DocumentLink>
          )}
        </DocumentColumn>

        <DetailsColumn>
          {analiseEmAndamento && (
            <AnalysisLoading>
              <Bars />
              <div>
                <strong>Documento em análise</strong>
                <span>
                  A IA está conferindo o documento e os dados do cadastro. O
                  resultado aparecerá aqui automaticamente.
                </span>
              </div>
            </AnalysisLoading>
          )}

          {!analiseExibida && (
            <Message type="info">
              Este cadastro ainda não possui uma análise de documento.
            </Message>
          )}

          {statusAnalise === 'error' && (
            <Message type="error">
              Não foi possível analisar o documento. Use “Refazer análise” para
              solicitar uma nova tentativa.
            </Message>
          )}

          {resultado && (
            <>
              <Metadata>
                <MetadataItem>
                  <span>Tipo de documento</span>
                  <strong>{resultado.tipo_documento || 'Indefinido'}</strong>
                </MetadataItem>
                <MetadataItem>
                  <span>Confiança da análise</span>
                  <strong>{resultado.confianca || 'Indefinida'}</strong>
                </MetadataItem>
              </Metadata>

              <SectionTitle>Verificações realizadas</SectionTitle>
              <Checklist>
                <thead>
                  <tr>
                    <th>Critério</th>
                    <th>Resultado</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {verificacoes.map(
                    ({ chave, rotulo, aprovado, motivo, estado }) => (
                      <tr key={chave}>
                        <td>{rotulo}</td>
                        <td>
                          <CheckResult $state={estado}>
                            {estado === 'approved' && (
                              <FiCheckCircle
                                size={20}
                                aria-label="Verificação aprovada"
                              />
                            )}
                            {estado === 'attention' && (
                              <FiAlertCircle
                                size={20}
                                aria-label="Verificação requer atenção"
                              />
                            )}
                            {estado === 'failed' && (
                              <FiXCircle
                                size={20}
                                aria-label="Verificação não aprovada"
                              />
                            )}
                          </CheckResult>
                        </td>
                        <td>
                          <Reason $state={estado}>
                            {MOTIVOS[motivo] ||
                              (aprovado
                                ? MOTIVOS.none
                                : 'Não informado nesta análise')}
                          </Reason>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </Checklist>

              {resultado.problemas && resultado.problemas.length > 0 && (
                <Problems $attentionOnly={statusAnalise === 'approved'}>
                  <SectionTitle>Pontos que precisam de atenção</SectionTitle>
                  <ul>
                    {resultado.problemas.map((problema) => (
                      <li key={problema}>{problema}</li>
                    ))}
                  </ul>
                </Problems>
              )}
            </>
          )}

          {statusAnalise === 'not_approved' && (
            <FeedbackSection>
              <SectionTitle>Feedback para o profissional</SectionTitle>
              <p>
                Revise a sugestão antes de enviá-la. O texto ficará disponível
                no app.
              </p>
              <Input
                componentClass="textarea"
                rows={4}
                maxLength={2000}
                value={feedback}
                onChange={setFeedback}
                placeholder="Explique o que precisa ser corrigido no cadastro"
              />
              <FeedbackActions>
                <Button
                  type="button"
                  appearance="primary"
                  startIcon={<FiSend />}
                  loading={enviando}
                  disabled={feedback.trim().length < 5}
                  onClick={enviarFeedback}
                >
                  Enviar feedback
                </Button>
                {ultimoFeedback && (
                  <small>
                    Último feedback:{' '}
                    {ultimoFeedback.lido_em
                      ? 'lido no app'
                      : 'aguardando leitura'}
                  </small>
                )}
              </FeedbackActions>
            </FeedbackSection>
          )}
        </DetailsColumn>
      </ContentLayout>
    </Card>
  );
}

AnaliseDocumentoCard.propTypes = {
  idProfissional: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  analise: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
    resultado: PropTypes.shape({
      feedback_sugerido: PropTypes.string,
      verificacoes: PropTypes.objectOf(PropTypes.bool),
      motivos_verificacoes: PropTypes.objectOf(
        PropTypes.oneOf([
          'none',
          'mismatch',
          'partial_match',
          'not_visible',
          'not_present',
          'uncertain',
        ])
      ),
      problemas: PropTypes.arrayOf(PropTypes.string),
      tipo_documento: PropTypes.string,
      confianca: PropTypes.string,
    }),
  }),
  documento: PropTypes.shape({
    arquivo: PropTypes.string,
    caminho: PropTypes.string,
  }),
  onAnaliseAtualizada: PropTypes.func,
};

AnaliseDocumentoCard.defaultProps = {
  idProfissional: undefined,
  analise: undefined,
  documento: undefined,
  onAnaliseAtualizada: undefined,
};
